import { queryStringUtil } from '../utils/QueryStringUtil';
import { Nullable } from '../types/custom/Nullable';
import { EmployeeDetail } from '../types/api/employees/EmployeeDetail';
import { UserDetail } from '../types/api/users/UserDetail';
import { PersonId } from '../types/api/primaryKeys/PrimaryKey';
import { parseAsId } from './IdParsing';
import { getCurrentUser, getRelatedPeopleForCurrentUser } from '../apiCalls/users/userApiCalls';
import { getEmployeeDetail } from '../apiCalls/employees/employeeApiCalls';


// The lifecycle of this class is important.
// It behaving correctly is dependent on it getting released from memory
// on full page navigations and logouts. Currently, that is the case.

class UserService {

    private _currentUser: Nullable<UserDetail>;
    private _userLinkedPeopleIds: Nullable<readonly PersonId[]>;
    private _userLinkedPerson: Nullable<EmployeeDetail>;

    public constructor() {
        // All fields intended to be private.
        // Use the "promise" versions to pull data.
        this._currentUser = null;       
        this._userLinkedPeopleIds = null;
        this._userLinkedPerson = null;
    }

    private _getActiveQueryString() {
        return sessionStorage.getItem('queryRequestedPersonId');
    }

    private _setActiveQueryString(personId: string) {
        sessionStorage.setItem('queryRequestedPersonId', personId);
    }

    public _initializeFromKnownPersonIdString(personIdString: string) {
        this._setActiveQueryString(personIdString);
    }

    /**
     * External services can call this function to make the service aware that a new query string
     * enrollee has been suggested. If no query string is provided, will search the current window context.
     * This method should be referenced at most once within the app.
     */
    public _initializeFromQueryString(queryString = undefined) {
        const enrolleeParam = queryStringUtil.getQueryParam('enrollee', queryString);

        // Only overwrite the current value of a query string person id if there is a non-null, non empty id.
        if(enrolleeParam !== null && enrolleeParam !== '') {
            this._initializeFromKnownPersonIdString(enrolleeParam);
        }
    }

    /**
     * Returns a promise capable of the authenticated user using the application.
     */
    public async currentUser() {
        // If the instance has a valid current user, just return that.
        if(this._currentUser !== null) {
            return this._currentUser;
        }

        // If the current user is null:

        try {
            const userData = await getCurrentUser();
            // Set the current instance's user data record so as to avoid
            // having to do this call in the future.
            this._currentUser = userData;
            // Then return the user record.
            return this._currentUser;
        }
        catch {
            // Implicitly handle unauthorized calls.
            return null;
        }
    }

    /**
     * Returns a promise to just the id component of a person, in situations where only that piece is necessary.
     * Will return null if there is no available person id.
     */
    public async currentPersonId() {
        const person = await this.currentPerson();
        return person ? person.personId : null;
    }


    /**
     * Returns a promise capable of fetching the person represented by this current session.
     * Note that the "person" is a distinct entity from the actual user, which has its own
     * access method.
     */
    public async currentPerson() {
        // If a person link has already been established, just return that.
        if(this._userLinkedPerson !== null) {
            return this._userLinkedPerson;
        }

        // If there is a present & active QS value that exists with the context,
        // we are going to default to only trying to use that id.
        const queryStringPersonId = this._getActiveQueryString();

        if(queryStringPersonId) {
            return await this._fetchPersonById(parseAsId<PersonId>(queryStringPersonId));
        }

        // If there is no query string value, instead get the people the current
        // user has access to.
        const peopleIds = await this._fetchUserLinkedPeopleIds();
        
        if (peopleIds === null) {
            return null;
        }
        // Take only the first element from the returned values.
        // This is because for now we will only assume one associated person per user.
        const personId = peopleIds[0];
        // If there were no returned people, just return null.
        if (!personId) {
            return null;
        }
        // Otherwise, fetch the first person by his id and return that.
        return await this._fetchPersonById(personId);
    }

    private async _fetchPersonById(personId: PersonId) {
        try {
            const person = await getEmployeeDetail(personId);
            this._userLinkedPerson = person;
            return this._userLinkedPerson;
        }
        catch {
            return null;
        }
    }

    private async _fetchUserLinkedPeopleIds() {
        if(this._userLinkedPeopleIds !== null) {
            return this._userLinkedPeopleIds;
        }

        try {
            const peopleData = await getRelatedPeopleForCurrentUser();
            this._userLinkedPeopleIds = peopleData;
            return this._userLinkedPeopleIds;
        }
        catch {
            return null;
        }
    }
}

export const userService = new UserService();